/**
 * Phase 10: ターン進行フェーズ（Puerto Rico 専用）
 *
 * ターンフラグをリセットし、次のターンに進みます。
 * 10ターン終了後はゲーム終了処理を実行。
 *
 * Puerto Rico 固有:
 * - アクションマーカーは使用しない
 * - 商品補充フェーズがないため、Phase 8（収入減少）から直接遷移
 * - ゲーム終了時は黒キューブペナルティを適用
 *
 * @see docs/specifications/logic/puerto_rico/game_end_override.md
 */

import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { PuertoRicoGameState } from '../../types';
import { endGamePuertoRico } from '../utils/gameEndPuertoRico';

export const advanceTurnPuertoRicoPhase: PhaseConfig<PuertoRicoGameState> = {
  // Phase開始時に自動実行
  onBegin: ({ G, events }: { G: PuertoRicoGameState; ctx: any; events: any }) => {
    debug.log(`[advanceTurnPuertoRicoPhase] ★★★ Phase 10: Advance Turn started ★★★`);

    // ターン番号はPhase 1 (issueShares) でインクリメントされる
    const currentActualTurn = G.actualTurn || 1;
    debug.log(`[advanceTurnPuertoRicoPhase] Current actualTurn: ${currentActualTurn}`);

    // ゲーム終了判定（Puerto Rico: 10ターン終了後）
    const maxTurns = 10;
    if (currentActualTurn >= maxTurns) {
      debug.log(`[advanceTurnPuertoRicoPhase] ★★★ Game ending (actualTurn ${currentActualTurn} >= ${maxTurns})`);
      // ゲーム終了処理（黒キューブペナルティ・勝敗判定）
      const result = endGamePuertoRico(G);
      debug.log(`[advanceTurnPuertoRicoPhase] Game ended: ${result.reason}, score: ${result.finalScore}`);

      // Boardgame.io にゲーム終了を通知
      events.endGame({
        winner: result.victory ? '0' : undefined,
        ...result
      });
      return;
    }

    debug.log(`[advanceTurnPuertoRicoPhase] Game continues (actualTurn ${currentActualTurn} < ${maxTurns})`);

    // プレイヤーのターンフラグをリセット
    const player = G.players['0'];
    if (player) {
      // アクション選択リセット
      player.selectedAction = null;

      // Phase 4 リセット
      player.tilesPlacedThisTurn = 0;
      player.newCityTilesPlacedThisTurn = 0;
      player.hasPassedBuild = false;
      player.buildTrackHistory = [];

      // Phase 5 リセット
      player.hasUpgradedLocoThisTurn = false;
      player.turnsCompletedThisPhase = 0;
      player.goodsTransportedThisPhase = 0;

      // 株式発行フラグリセット
      player.hasExecutedIssueSharesMove = false;
    }

    // アクション選択完了フラグをリセット
    G.isActionSelectionComplete = false;

    debug.log(`[advanceTurnPuertoRicoPhase] Turn flags reset, ready for next turn`);

    // 処理結果を保存（UI表示用）- 次ターン番号を表示
    const nextTurn = currentActualTurn + 1;
    G.autoPhaseResult = {
      phase: 'advanceTurn',
      message: 'Advancing to next turn...',
      floatingText: `Turn ${nextTurn}`,
      details: { newTurn: nextTurn },
    };

    debug.log('[advanceTurnPuertoRicoPhase] ★ Auto-processing complete, waiting for user click');
  },

  moves: {
    // ユーザークリックでフェーズ進行
    proceedAutoPhase: ({ G, events }: { G: PuertoRicoGameState; events: any }) => {
      G.autoPhaseResult = null;
      events.endPhase();
    },
  },

  onEnd: ({ G }: { G: PuertoRicoGameState }) => {
    debug.log('[advanceTurnPuertoRicoPhase] ★★★ onEnd called - Phase 10 ended ★★★');
    debug.log(`[advanceTurnPuertoRicoPhase] ★★★ actualTurn: ${G.actualTurn} (next turn will be ${G.actualTurn + 1})`);

    // 自動フェーズ履歴に追加（UIアニメーション用）
    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('advanceTurn');
  },

  // 次フェーズへ遷移（Phase 1に戻る）
  next: 'issueShares'
};
